import { useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import countriesBorder from '../../static/data/countriesBorder.json';
import countriesShortInfo from '../../static/data/countriesShortInfo.json';
import { useProfile } from './useProfileStores';
import { useUiUnitTypeValue } from './useUiStore';
import { appConfig } from '../../settings';
import {
    ICardSetRevenueItem,
    ICardsRevenueItem,
    IChartData,
    IChartTitle,
    ICompanyRevenueItem,
    ICountryRevenueItem,
    ICountryUsageListItem,
    IDashboardActivityRoll,
    IDashboardCountryUsageList,
    IFilterParams,
    IFinancesRollCardsHook,
    IGetApiType,
    IGoogleGeoPosition,
    IJSONCountryInfo,
    IJsonCountryBorder,
    IJsonCountryBorderPoint,
    IJsonCountryBorderPoints,
    IOperatorRevenueItem,
    IOverlayView,
    IStatItem,
    ITimelineRevenueItem,
    ITimelineZoneItem,
    IUsageActivityUnitType,
    IUsageData,
    IUseDashboardCountryUsageCountryInfo,
    IUseDashboardCountryUsageCountryPolygon,
    IUseDashboardCountryUsageCountryPolygonsList,
} from '../../interfaces';
import { UnitTypeValues } from '../../middleware';
import {
    IMakeRowTitle,
    byteRange1000,
    getApiTypeWithUnitType,
    getChartItemTitle,
    jsonFieldToNumber,
    mapRevenues2Chart,
    showOriginalUnitByType,
} from '../../instruments';
import { ITrPage, tI, tP } from '../../translate';

export const getUsageChartTitle = (itemName: ITrPage): IChartTitle => {
    return [tP(itemName), tI('Usage'), { role: 'annotation' }, { role: 'annotationText' }];
};

const useDashboardUsageDataByAPI = (apiTypeId: IGetApiType, unitType?: UnitTypeValues): Array<any> => {
    const apiName = getApiTypeWithUnitType(apiTypeId, unitType);
    return useContext(MobXProviderContext).RootStore.dashboardStore.usageData.get(apiName) || [];
};

// ------------ Activity Usage ------------

export function useDashboardActivityUsage(): IDashboardActivityRoll {
    const activityUsage = useContext(MobXProviderContext).RootStore.dashboardStore.usageActivity;
    const data: IDashboardActivityRoll = {
        cardsCount: activityUsage.cardsCount,
        cardsRatio: activityUsage.cardsRatio,
        costRatio: activityUsage.costRatio,
        costs: activityUsage.costs,
    };

    activityUsage.statsByUnitType.forEach((i: IUsageActivityUnitType) => {
        switch (i.unitType) {
            case 'sms':
                data.sms = i.volume;
                data.smsRatio = i.volumeRatio;
                break;
            case 'sec':
                data.voice = i.volume;
                data.voiceRatio = i.volumeRatio;
                break;
            case 'byte':
                data.originalUnits = i.volume;
                data.originalUnitsRatio = i.volumeRatio;
                break;
            // case 'unit':
            //     break;
            default:
                break;
        }
    });
    return data;
}

// ------------ Finances Usage ------------
export function useDashboardFinancesRollCards(): IFinancesRollCardsHook {
    const cards = useContext(MobXProviderContext).RootStore.dashboardStore.financesRollCards;
    const cardsInfo: IFinancesRollCardsHook = { active: 0, inactive: 0, total: 0, pieActive: 0 };
    cardsInfo.active = cards.active;
    cardsInfo.inactive = cards.inactive;
    cardsInfo.total = cards.active + cards.inactive;
    cardsInfo.pieActive =
        cardsInfo.active && (cardsInfo.active / cardsInfo.total) * 100 < 0.1
            ? cardsInfo.total / 1000
            : cardsInfo.active;
    return cardsInfo;
}
export function useDashboardFinancesRollStats(): Array<IStatItem> {
    return useContext(MobXProviderContext).RootStore.dashboardStore.financesRollStats;
}
export function useDashboardFinancesRollVolumes(): string {
    return useContext(MobXProviderContext).RootStore.dashboardStore.financesRollVolume;
}
// ------------ CountryUsage ------------
export function useDashboardCountryUsageForMapList(): IDashboardCountryUsageList {
    const unitType = UnitTypeValues.DATA;
    const countryUsageList = useContext(MobXProviderContext).RootStore.dashboardStore.usageData.get(
        `GET_USAGE_COUNTRY_LIST-${unitType}`,
    );
    if (!countryUsageList || (!!countryUsageList && !Array.isArray(countryUsageList))) {
        return [];
    }

    return countryUsageList.map((item: IUsageData) => {
        return {
            countryId: item.country_id,
            countryName: item.name,
            countryIso3: item.iso3,
            originalUnits: item.volume,
        };
    });
}

function useCountriesShortInfoList(): Array<IJSONCountryInfo> {
    return countriesShortInfo;
}

export function useDashboardCountriesUsageForMapInfo(): Array<IUseDashboardCountryUsageCountryInfo> {
    const countriesInfoList = useCountriesShortInfoList();

    const countryUsageList = useDashboardCountryUsageForMapList();
    return countryUsageList.map((c: ICountryUsageListItem) => {
        const countryInfo = countriesInfoList.find(
            (country: IJSONCountryInfo) => country.alpha3 === c.countryIso3 || country.ioc === c.countryIso3,
        );

        const border: IGoogleGeoPosition[] = (
            (countriesBorder.features
                .find((mc) => mc.id === c.countryIso3)
                ?.geometry?.coordinates.flat(1) as IJsonCountryBorderPoints) || []
        ).map((point) => {
            const p = point as IJsonCountryBorderPoint;

            return { lat: p[1], lng: p[0] };
        });

        const geo = {
            center: {
                lat: jsonFieldToNumber(countryInfo?.geo?.center.lat),
                lng: jsonFieldToNumber(countryInfo?.geo?.center.lng),
            },

            border,
        };

        return {
            id: Number(c.countryId),
            code: c.countryIso3,
            name: c.countryName,
            originalUnits: c.originalUnits,
            textUnits: c.originalUnits && byteRange1000(c.originalUnits),
            geo,
        };
    });
}

export function useDashboardCountriesUsageForMapPolygons(): IUseDashboardCountryUsageCountryPolygonsList {
    const countryUsageList = useDashboardCountryUsageForMapList();
    const polygons: IUseDashboardCountryUsageCountryPolygonsList = [];
    const colorIntensity = (x: number): number => x ** (1 / 5) / 100;

    countryUsageList.forEach((c: ICountryUsageListItem) => {
        const fillColor = `rgba(0, 0, 255, ${c.originalUnits ? colorIntensity(+c.originalUnits) : 0})`;
        const polygon: IUseDashboardCountryUsageCountryPolygon = {
            id: c.countryId || 0,
            polygonId: `${c.countryId}`,
            code: c.countryIso3,
            name: c.countryName,
            originalUnits: c.originalUnits,
            textUnits: c.originalUnits && byteRange1000(c.originalUnits),
            fillColor,
        };
        const country = countriesBorder.features.find((mc) => mc.id === c.countryIso3);

        if (country?.geometry?.coordinates.length && country?.geometry?.type === 'Polygon') {
            const border: Array<IGoogleGeoPosition> =
                country.geometry.coordinates?.[0].map((p: any) => ({ lat: p[1], lng: p[0] })) || [];
            if (border.length) {
                polygons.push({ ...polygon, geo: { border } });
            }
        } else if (country?.geometry?.coordinates.length && country?.geometry?.type === 'MultiPolygon') {
            const countryBorder = (country.geometry.coordinates.flat(1) as IJsonCountryBorder) || [];
            countryBorder.forEach((borders, index: number): void => {
                const manyBorders = (borders as IJsonCountryBorderPoints) || [];
                polygons.push({
                    ...polygon,
                    polygonId: `${c.countryId}-${index}`,
                    geo: {
                        border: manyBorders.map((b: IJsonCountryBorderPoint) => ({ lat: b[1], lng: b[0] })),
                    },
                });
            });
        } else {
            process.env.NODE_ENV === 'development' && console.log(Date.now(), 'NOT POLYGON', polygon.name, country);
        }
    });

    return polygons;
}

export function useDashboardCountryUsageChart(apiTypeId: IGetApiType, unitType?: UnitTypeValues): IChartData {
    const unitTypes = useUiUnitTypeValue();
    const countryUsageList = useDashboardUsageDataByAPI(apiTypeId, unitType);
    if (!countryUsageList.length) {
        return [];
    }

    const countryUsageData: IChartData = countryUsageList
        .map((i: IUsageData) => i)
        .sort((a: IUsageData, b: IUsageData) => Number(b.volume) - Number(a.volume))
        .filter((_: IUsageData, index: number) => index < appConfig.top10)
        .map((item: IUsageData) => {
            const title = getChartItemTitle(item, 'country_id');
            const showUnit = showOriginalUnitByType(`${item.volume}`, unitTypes);
            return [title, Number(item.volume), showUnit, item.country_id];
        });
    countryUsageData.unshift(getUsageChartTitle('Country'));

    return countryUsageData;
}

// ------------ CardSetUsage ------------
export function useDashboardCardSetUsageChart(apiTypeId: IGetApiType, unitType?: UnitTypeValues): IChartData {
    const cardSetUsageList = useDashboardUsageDataByAPI(apiTypeId, unitType);
    const unitTypes = useUiUnitTypeValue();
    if (!cardSetUsageList.length) {
        return [];
    }

    const cardSetUsageData: IChartData = cardSetUsageList.map((item: IUsageData) => {
        const title = getChartItemTitle(item, 'card_set_id');
        const showUnit = showOriginalUnitByType(`${item.volume}`, unitTypes);
        return [title, Number(item.volume), showUnit, item.card_set_id];
    });

    cardSetUsageData.unshift(getUsageChartTitle('Card Set'));
    return cardSetUsageData;
}

// ------------ Cards Usage ------------

export function useDashboardCardsUsageChart(apiTypeId: IGetApiType, unitType?: UnitTypeValues): IChartData {
    const cardsUsageList = useDashboardUsageDataByAPI(apiTypeId, unitType);
    const unitTypes = useUiUnitTypeValue();
    if (!cardsUsageList.length) {
        return [];
    }

    const cardsUsageData: IChartData = cardsUsageList.map((item: IUsageData) => {
        const title = getChartItemTitle(item, 'card_id');
        const showUnit = showOriginalUnitByType(`${item.volume}`, unitTypes);
        return [title, Number(item.volume), showUnit, item.card_id];
    });
    cardsUsageData.unshift(getUsageChartTitle('Card'));
    return cardsUsageData;
}

// ------------ Cards Usage ------------
export function useDashboardTimelineUsageChart(): IChartData {
    const timelineUsageList = useContext(MobXProviderContext).RootStore.dashboardStore.timelineUsageList;
    const unitTypes = useUiUnitTypeValue();

    const timelineUsageData = timelineUsageList.map((item: IUsageData) => {
        const showUnit = showOriginalUnitByType(`${item.volume}`, unitTypes);
        return [item.month, Number(item.volume), showUnit];
    });
    timelineUsageData.unshift(['', 'Usage', { role: 'annotation' }]);
    return timelineUsageData;
}

// ------------ CompanyUsage ------------
export function useDashboardCompanyUsageChart(): IChartData {
    const companyUsageList = useContext(MobXProviderContext).RootStore.dashboardStore.companyUsageList;

    const unitTypes = useUiUnitTypeValue();
    const companyUsageData = companyUsageList.map((item: IUsageData) => {
        const showUnit = showOriginalUnitByType(`${item.volume}`, unitTypes);
        return [item.name, Number(item.volume), showUnit, item.company_id];
    });
    companyUsageData.unshift(getUsageChartTitle('Company'));
    return companyUsageData;
}

// ------------ ZoneUsage ------------
export function useDashboardZoneUsageChart(): IChartData {
    const zoneUsageList = useContext(MobXProviderContext).RootStore.dashboardStore.zoneUsageList;

    const unitTypes = useUiUnitTypeValue();

    const zoneUsageData = zoneUsageList.map((item: IUsageData) => {
        const title = getChartItemTitle(item, 'zone_id');
        const showUnit = showOriginalUnitByType(`${item.volume}`, unitTypes);
        return [title, Number(item.volume), showUnit, item.zone_id];
    });
    zoneUsageData.unshift(getUsageChartTitle('Zone'));
    return zoneUsageData;
}

// ------------ ZoneUsage ------------
export function useDashboardTelecomUsageChart(): IChartData {
    const telecomUsageList = useContext(MobXProviderContext).RootStore.dashboardStore.telecomUsageList;

    const unitTypes = useUiUnitTypeValue();
    const telecomUsageData = telecomUsageList.map((item: IUsageData) => {
        const title = getChartItemTitle(item, 'operator_id');
        const showUnit = showOriginalUnitByType(`${item.volume}`, unitTypes);
        return [title, Number(item.volume), showUnit, item.operator_id];
    });
    telecomUsageData.unshift(getUsageChartTitle('Telecom'));
    return telecomUsageData;
}

// ------------ Company Revenue ------------
export function useDashboardCompaniesRevenueChart(): IChartData {
    const { isCompanyTypeIdMNOHost } = useProfile();
    const companyRevenueList = useContext(MobXProviderContext).RootStore.dashboardStore.companyRevenueList;
    const makeRowTitle: IMakeRowTitle = (i: ICompanyRevenueItem) => {
        return [i.name ? i.name : i.companyId ? `#${i.companyId}` : 'Unidentified', i.companyId ? i.companyId : ''];
    };
    return mapRevenues2Chart({
        isHideCost: isCompanyTypeIdMNOHost,
        makeRowTitle: makeRowTitle,
        revenues: companyRevenueList,
        title: 'Company',
    });
}

// ------------ CardSet Revenue ------------
export function useDashboardCardSetRevenueChart(): IChartData {
    const { isCompanyTypeIdMNOHost } = useProfile();
    const cardSetRevenueList = useContext(MobXProviderContext).RootStore.dashboardStore.cardSetRevenueList;
    const makeRowTitle: IMakeRowTitle = (i: ICardSetRevenueItem) => {
        return [
            i.name ? i.name : i.cardSetId ? i.cardSetId.toString() : 'Unidentified',
            i.cardSetId ? i.cardSetId : '',
        ];
    };
    return mapRevenues2Chart({
        isHideCost: isCompanyTypeIdMNOHost,
        makeRowTitle: makeRowTitle,
        revenues: cardSetRevenueList,
        title: 'Card Set',
    });
}

export function useDashboardCountryRevenueChart(): IChartData {
    const { isCompanyTypeIdMNOHost } = useProfile();
    const countryRevenueList = useContext(MobXProviderContext).RootStore.dashboardStore.countryRevenueList;
    const makeRowTitle: IMakeRowTitle = (i: ICountryRevenueItem) => {
        return [
            i.name ? i.name : i.iso3 ? i.iso3 : i.countryId ? `#${i.countryId}` : 'Unidentified',
            i.countryId ? i.countryId : '',
        ];
    };
    return mapRevenues2Chart({
        isHideCost: isCompanyTypeIdMNOHost,
        makeRowTitle: makeRowTitle,
        revenues: countryRevenueList,
        title: 'Country',
    });
}

// ------------ Cards Revenue ------------
export function useDashboardCardsRevenueChart(): IChartData {
    const { isCompanyTypeIdMNOHost } = useProfile();
    const cardsRevenueList = useContext(MobXProviderContext).RootStore.dashboardStore.cardsRevenueList;

    const makeRowTitle: IMakeRowTitle = (i: ICardsRevenueItem) => {
        return [
            // i.printedId ? i.printedId.slice(-6) : i.name ? i.name : i.eid ? i.eid.slice(-6) : 'Unidentified',
            i.cardId && i.name
                ? `${i.name}-#${i.cardId}`
                : i.name
                ? i.name
                : i.printedId
                ? i.printedId.slice(-6)
                : i.eid
                ? i.eid.slice(-6)
                : i.cardId
                ? `#${i.cardId}`
                : 'Unidentified',
            i.cardId ? i.cardId : '',
        ];
    };
    return mapRevenues2Chart({
        isHideCost: isCompanyTypeIdMNOHost,
        makeRowTitle: makeRowTitle,
        revenues: cardsRevenueList,
        title: 'Card',
    });
}

// ------------ Zone Revenue ------------
export function useDashboardZoneRevenueChart(): IChartData {
    const { isCompanyTypeIdMNOHost } = useProfile();
    const zoneRevenueList = useContext(MobXProviderContext).RootStore.dashboardStore.zoneRevenueList;
    const makeRowTitle: IMakeRowTitle = (i: ITimelineZoneItem) => {
        return [i.name ? i.name : i.zoneId ? `#${i.zoneId}` : 'Unidentified', i.zoneId ? i.zoneId : ''];
    };
    return mapRevenues2Chart({
        isHideCost: isCompanyTypeIdMNOHost,
        makeRowTitle: makeRowTitle,
        revenues: zoneRevenueList,
        title: 'Zone',
    });
}

// ------------ Operator/Telecom Revenue ------------
export function useDashboardTelecomRevenueChart(): IChartData {
    const { isCompanyTypeIdMNOHost } = useProfile();
    const telecomRevenueList = useContext(MobXProviderContext).RootStore.dashboardStore.telecomRevenueList;
    const makeRowTitle: IMakeRowTitle = (i: IOperatorRevenueItem) => {
        return [i.name ? i.name : i.operatorId ? `#${i.operatorId}` : 'Unidentified', i.operatorId ? i.operatorId : ''];
    };
    return mapRevenues2Chart({
        isHideCost: isCompanyTypeIdMNOHost,
        makeRowTitle: makeRowTitle,
        revenues: telecomRevenueList,
        title: 'Operator',
    });
}

// ------------ Timeline Revenue ------------
export function useDashboardTimelineRevenueChart(): IChartData {
    const { isCompanyTypeIdMNOHost } = useProfile();
    const timelineRevenueList = useContext(MobXProviderContext).RootStore.dashboardStore.timelineRevenueList;
    const makeRowTitle: IMakeRowTitle = (i: ITimelineRevenueItem) => {
        return [
            i.month ? i.month : i.startDate ? i.startDate : 'Unidentified',
            i.month ? i.month : i.startDate ? i.startDate : '',
        ];
    };
    return mapRevenues2Chart({
        isHideCost: isCompanyTypeIdMNOHost,
        makeRowTitle: makeRowTitle,
        revenues: timelineRevenueList,
        title: 'Revenue',
    });
}

export function useDashboardFilterParamsFinance(): IFilterParams {
    return useContext(MobXProviderContext).RootStore.dashboardStore.filterParamsFinance;
}

export function useDashboardOverlayView(): IOverlayView {
    return useContext(MobXProviderContext).RootStore.dashboardStore.mapOverlayView;
}
